'use client';

import {useEffect, useRef, useState} from 'react';
import Link from 'next/link';
import {Search} from 'lucide-react';
import {CompanyLogo} from 'iq-interview_guides/components/company_logo';
import {searchTypeahead} from 'iq-companies/api';
import {trackHeroSearchResultClicked} from 'iq-companies/tracking';
import type {CompanySearchResult} from 'iq-companies/types';
import {RequestCompanyForm} from './request_company_form';

const DEBOUNCE_MS = 200;

function resultHref(r: CompanySearchResult): string {
  return r.type === 'company' ? `/interview-guides/${r.slug}` : `/guides/${r.slug}`;
}

// Hero-level typeahead — the "find any company, including the long tail" path (spec
// module 2). Distinct from filter_bar.tsx's chips, which narrow the feed in place
// rather than navigating away. Debounced against the real GET /v1/companies/search
// (plan § 1d).
export function CompaniesSearchBar() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CompanySearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setLoading(false);
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    const timeout = setTimeout(async () => {
      const found = await searchTypeahead(q);
      if (requestIdRef.current === requestId) {
        setResults(found);
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const showDropdown = open && query.trim().length > 0;

  return (
    <div ref={containerRef} className='relative w-full'>
      <div className='relative'>
        <Search className='pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
        <input
          type='text'
          placeholder='Search any company or role…'
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className='w-full rounded-xl border border-gray-200 py-3.5 pl-12 pr-4 text-base shadow-sm focus:border-iq-orange focus:outline-none focus:ring-2 focus:ring-iq-orange/40'
        />
      </div>
      {showDropdown && (
        <div className='absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg'>
          {loading ? (
            <p className='px-4 py-3 text-sm text-gray-500'>Searching…</p>
          ) : results.length > 0 ? (
            results.map((r) => (
              <Link
                key={`${r.type}-${r.slug}`}
                href={resultHref(r)}
                className='flex items-center gap-3 px-4 py-3 transition-colors hover:bg-gray-50'
                onClick={() => {
                  setOpen(false);
                  void trackHeroSearchResultClicked(r.type, r.slug);
                }}>
                {r.type === 'company' ? (
                  <CompanyLogo
                    companyWebsite={r.company_website ?? ''}
                    companyName={r.name}
                    size={28}
                    className='rounded object-contain'
                  />
                ) : (
                  <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-medium text-gray-500'>
                    role
                  </span>
                )}
                <span className='min-w-0 flex-1'>
                  <span className='block truncate text-sm font-medium text-gray-900'>{r.name}</span>
                  <span className='block text-xs capitalize text-gray-500'>{r.type}</span>
                </span>
              </Link>
            ))
          ) : (
            <RequestCompanyForm defaultName={query} />
          )}
        </div>
      )}
    </div>
  );
}
